'use client'

import { useEffect, useRef } from 'react'

/** `12 000` → "12 000", `82,4` → "82,4": the comp's own formatter. */
function format(value: number, decimals: number, grouped: boolean): string {
  const [whole = '0', fraction] = value.toFixed(decimals).split('.')
  const head = grouped ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : whole
  return fraction ? `${head},${fraction}` : head
}

/**
 * A figure that counts up from zero the first time it is on screen.
 *
 * The comp's counter, as it runs there: 2.2s with a quartic ease-out, so the
 * number races and then settles onto its value; starts when a third of it is
 * visible. The server paints the final value — a reader without script, a
 * crawler and a reduced-motion visitor all get the real number.
 */
export function CountUp({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (!('IntersectionObserver' in window)) return

    const grouped = /\s| /.test(value)
    const clean = value.replace(/[\s ]/g, '').replace(',', '.')
    const target = Number.parseFloat(clean)
    if (Number.isNaN(target)) return
    const decimals = (clean.split('.')[1] ?? '').length
    el.textContent = decimals ? `0,${'0'.repeat(decimals)}` : '0'

    let frame = 0
    const run = () => {
      const start = performance.now()
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / 2200)
        el.textContent = t < 1 ? format(target * (1 - (1 - t) ** 4), decimals, grouped) : value
        if (t < 1) frame = requestAnimationFrame(step)
      }
      frame = requestAnimationFrame(step)
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        io.disconnect()
        run()
      },
      { threshold: 0.35 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      cancelAnimationFrame(frame)
      el.textContent = value
    }
  }, [value])

  return (
    <span ref={ref} data-count="">
      {value}
    </span>
  )
}
